import { RootStore } from "@intrasync/stores";
import type { RootStoreOptions } from "@intrasync/stores";
import apiClient from "./api-client/apiClient";
import rootStoreConfig from "./rootStoreConfig";

interface Window {
  rootStore?: RootStore;
  dateFNS?: unknown;
  __STORE_COUNT__?: number;
  __MEMORY_MONITOR__?: unknown;
}

type ApiClientMode = Parameters<typeof apiClient>[0];

/**
 * Build the Feathers `app` the published @intrasync/stores RootStore requires.
 *
 * The library intentionally no longer constructs its own Feathers client (that
 * logic was lifted out so the package stays app-agnostic); its constructor
 * throws unless an `app` is injected via `props.app`. We build it here with the
 * app-local apiClient, mirroring the auth-storage key derivation the RootStore
 * performs internally so socket auth reads/writes the same keys.
 */
function buildApp(config: RootStoreOptions) {
  const mode = (config.mode ?? "dev") as ApiClientMode;
  const isProdMode = ["production", "prod", "live"].includes(mode);
  const authStorageKey = `feathers-jwt${isProdMode ? "" : `-${mode}`}`;
  const refreshStorageKey = `refresh-token${isProdMode ? "" : `-${mode}`}`;

  return apiClient(mode, {
    ...(config.apiOptions as object),
    authStorageKey,
    refreshStorageKey,
  });
}

export function createRootStore(): RootStore {
  const rootStore = new RootStore({
    ...rootStoreConfig,
    app: buildApp(rootStoreConfig),
  });

  // The published RootStore only eagerly creates the first couple of
  // service-store batches in its constructor and defers the rest to a
  // setTimeout. That left later service stores (e.g. the accounting stores)
  // unregistered when their route first rendered, so injectStores threw
  // "Store '<name>' is not available". ServiceStoreManager.initializeBatch has
  // a synchronous body, so eagerly create every batch here — mirroring the
  // local RootStore — to guarantee all service stores exist before first render.
  const serviceStoreManager = rootStore.serviceStoreManager;
  if (serviceStoreManager) {
    for (let i = 0; i < serviceStoreManager.totalBatches; i++) {
      serviceStoreManager.initializeBatch(i);
    }
  }

  // if (import.meta.env.MODE === "development") {
  (window as Window).rootStore = rootStore;
  // }

  return rootStore;
}
